import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Grid,
    List,
    Layout,
    TrendingDown,
    CheckCircle,
    Clock,
    AlertCircle,
    BarChart,
    ChevronDown,
    ChevronUp,
    Tag,
    Filter,
    Search,
    DollarSign,
    FileText,
    Package,
    Activity,
    Link,
    Circle
} from 'react-feather';
import api, { taskApi, taskApiV2, featureTagApi, projectApi, releasesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { IconButton } from '../components/ui/IconButton';
import { FeatherArrowLeft } from '../components/ui/icons';
import { isManager } from '../utils/roleUtils';
import TaskForm from '../components/Tasks/TaskForm';
import TaskList from '../components/Tasks/TaskList';
import TagBar from '../components/FeatureTags/TagBar';
import FeatureTagStats from '../components/FeatureTags/FeatureTagStats';
import TaskFeatureTimeline from '../components/FeatureTags/TaskFeatureTimeline';
import TaskSummaryModal from './components/TaskSummaryModal';
import BillingTab from '../components/Projects/BillingTab';
import ReleaseManagementStatsTab from '../components/Projects/ReleaseManagementStatsTab';
import ProjectNotesArtifacts from '../components/Projects/ProjectNotesArtifacts';
import TaskTypeSelect from '../components/shared/TaskTypeSelect';
import { DEFAULT_TASK_TYPE, TASK_TYPE_SUBTASK } from '../constants/taskTypes';
import config from '../config/setup';
import './ProjectDetailsV2.css';

const TASKS_PER_PAGE = config.projectDetailsV2?.tasksPerPage ?? 20;

const GROUPING_MODE_MAP = {
    'feature-tag': 'by-features',
    'work-plan': 'by-work-plan',
    'releases': 'by-releases',
    'task-links': 'by-links'
};

const GROUPING_MODE_LABELS = {
    'none': 'Ungrouped',
    'feature-tag': 'By Features',
    'work-plan': 'PickedUp Today',
    'releases': 'By Releases',
    'task-links': 'By Links'
};

function getTodayDate() {
    return new Date().toISOString().split('T')[0];
}

/** Build tree-ordered task list: roots first, each root followed by its subtasks (from linked_tasks with direction 'subtask'). */
function buildTaskTreeOrder(tasks) {
    const subtaskIds = new Set();
    const idStr = (id) => (id != null ? String(id) : null);
    tasks.forEach(task => {
        (task.linked_tasks || []).forEach(link => {
            if (link.direction === 'subtask' && link.linked_task?.id) subtaskIds.add(idStr(link.linked_task.id));
        });
    });
    const roots = tasks.filter(t => !subtaskIds.has(idStr(t.id)));
    const ordered = [];
    roots.forEach(root => {
        ordered.push(root);
        (root.linked_tasks || []).forEach(link => {
            if (link.direction === 'subtask' && link.linked_task) {
                const full = tasks.find(t => idStr(t.id) === idStr(link.linked_task.id));
                if (full) ordered.push(full);
            }
        });
    });
    return { ordered, subtaskIds };
}

const ProjectDetailsV2 = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [users, setUsers] = useState([]);
    const [featureTags, setFeatureTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [createSubtaskForTaskId, setCreateSubtaskForTaskId] = useState(null);
    const { user } = useAuth();
    const [userIsManager, setUserIsManager] = useState(false);

    // V2 state: groups and paginated tasks
    const [groups, setGroups] = useState([]);
    const [groupTasks, setGroupTasks] = useState({}); // { [groupId]: { tasks, page, hasMore, totalCount, tag/user/release/withLinks } }
    const [ungroupedTasks, setUngroupedTasks] = useState([]);
    const [ungroupedPage, setUngroupedPage] = useState(1);
    const [ungroupedHasMore, setUngroupedHasMore] = useState(false);
    const [ungroupedTotalCount, setUngroupedTotalCount] = useState(0);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [loadingMoreGroupId, setLoadingMoreGroupId] = useState(null);
    const [loadingMoreFlat, setLoadingMoreFlat] = useState(false);

    // New state for enhanced functionality
    const [viewMode, setViewMode] = useState('compact');
    const [subtaskBubblesMode, setSubtaskBubblesMode] = useState(false);
    const [filters, setFilters] = useState({
        status: [],
        createdBy: '',
        assignedTo: '',
        taskType: '',
        withLinksOnly: false
    });
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [selectedTagId, setSelectedTagId] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');
    const [statsCollapsed, setStatsCollapsed] = useState(true);
    const [tagBarCollapsed, setTagBarCollapsed] = useState(true);
    const [taskControlsCollapsed, setTaskControlsCollapsed] = useState(false);
    const [featureTagStatsCollapsed, setFeatureTagStatsCollapsed] = useState(true);
    const [taskTimelineCollapsed, setTaskTimelineCollapsed] = useState(true);
    const [selectedTaskForModal, setSelectedTaskForModal] = useState(null);
    const [activeTab, setActiveTab] = useState('tasks');
    const [groupingMode, setGroupingMode] = useState('none');
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [releases, setReleases] = useState([]);
    const statusDropdownRef = useRef(null);
    const taskFormTitleRef = useRef(null);

    // Debounce search query to avoid firing API on every keystroke
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const filterParams = useMemo(() => ({
        status: filters.status,
        createdBy: filters.createdBy || undefined,
        assignedTo: filters.assignedTo || undefined,
        taskType: filters.taskType || undefined,
        searchQuery: debouncedSearchQuery.trim() || undefined,
        withLinksOnly: filters.withLinksOnly || undefined,
        selectedTagId: selectedTagId || undefined,
        sortOrder,
        date: groupingMode === 'work-plan' ? getTodayDate() : undefined
    }), [filters, debouncedSearchQuery, selectedTagId, sortOrder, groupingMode]);

    const checkUserRole = useCallback(async () => {
        try {
            const response = await api.get('/api/auth/role');
            const userRole = response.data?.role;
            setUserIsManager(isManager(userRole));
        } catch (err) {
            console.error('Error checking user role:', err);
            setError('Failed to check user permissions');
        }
    }, []);

    const fetchProjectData = useCallback(async () => {
        try {
            setLoading(true);
            const projectResponse = await api.get(`/api/projects/${projectId}`);
            if (!projectResponse.data) {
                navigate('/projects');
                return;
            }
            setProject(projectResponse.data);
            setError('');
        } catch (err) {
            console.error('Error fetching project data:', err);
            if (err.response?.status === 404) {
                navigate('/projects');
                return;
            }
            setError('Failed to fetch project data');
        } finally {
            setLoading(false);
        }
    }, [projectId, navigate]);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await api.get('/api/users');
            setUsers(response.data || []);
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    }, []);

    const fetchFeatureTags = useCallback(async () => {
        try {
            const data = await featureTagApi.getByProject(projectId);
            setFeatureTags(data || []);
        } catch (err) {
            console.error('Error fetching feature tags:', err);
        }
    }, [projectId]);

    const fetchReleases = useCallback(async () => {
        try {
            const data = await releasesApi.getProjectReleases(projectId);
            setReleases(data || []);
        } catch (err) {
            console.error('Error fetching releases:', err);
            setReleases([]);
        }
    }, [projectId]);

    useEffect(() => {
        if (!projectId) {
            navigate('/projects');
            return;
        }
        if (user?.id) {
            checkUserRole();
            fetchProjectData();
            fetchUsers();
            fetchFeatureTags();
            fetchReleases();
        }
    }, [projectId, user?.id, navigate, fetchProjectData, fetchUsers, fetchFeatureTags, fetchReleases, checkUserRole]);

    // Fetch groups when groupingMode is feature-tag, work-plan, releases, or task-links
    useEffect(() => {
        if (!projectId || groupingMode === 'none') {
            setGroups([]);
            setGroupTasks({});
            setExpandedGroups(new Set());
            return;
        }
        const apiGroupingMode = GROUPING_MODE_MAP[groupingMode];
        if (!apiGroupingMode) return;

        let cancelled = false;
        setGroupsLoading(true);
        setGroupTasks({});
        setGroups([]);
        setExpandedGroups(new Set()); // Keep all task-group headers collapsed during load

        taskApiV2.getGroups(projectId, apiGroupingMode, filterParams)
            .then(({ groups: gs }) => {
                if (cancelled) return;
                setGroups(gs || []);
                setGroupsLoading(false);
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error('Error fetching groups:', err);
                    setGroupsLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [projectId, groupingMode, filterParams]);

    // Fetch first page of ungrouped tasks when groupingMode is 'none'
    useEffect(() => {
        if (!projectId || groupingMode !== 'none') {
            setUngroupedTasks([]);
            setUngroupedPage(1);
            setUngroupedHasMore(false);
            setUngroupedTotalCount(0);
            setTasksLoading(false);
            setExpandedGroups(new Set());
            return;
        }

        let cancelled = false;
        setTasksLoading(true);
        setUngroupedTasks([]);
        setUngroupedPage(1);

        taskApiV2.getProjectTasks(projectId, {
            ...filterParams,
            page: 1,
            limit: TASKS_PER_PAGE
        })
            .then(({ tasks, totalCount, hasMore }) => {
                if (cancelled) return;
                setUngroupedTasks(tasks || []);
                setUngroupedHasMore(hasMore || false);
                setUngroupedTotalCount(totalCount || 0);
                setUngroupedPage(1);
                setTasksLoading(false);
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error('Error fetching ungrouped tasks:', err);
                    setTasksLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [projectId, groupingMode, filterParams]);

    // Reset groupTasks and ungroupedTasks when filters/sortOrder/selectedTagId change
    useEffect(() => {
        setGroupTasks({});
        setUngroupedTasks([]);
        setUngroupedPage(1);
        setUngroupedHasMore(false);
        setUngroupedTotalCount(0);
    }, [filters, sortOrder, selectedTagId, debouncedSearchQuery]);

    const toggleGroup = useCallback((groupId) => {
        const gt = groupTasks[groupId];
        const needsFetch = !gt || !gt.tasks?.length;

        if (needsFetch) {
            // Group has no tasks yet: fetch first, then expand only after load completes (keep collapsed during load)
            const apiGroupingMode = GROUPING_MODE_MAP[groupingMode];
            if (!apiGroupingMode || !projectId) return;

            taskApiV2.getGroupTasks(projectId, apiGroupingMode, groupId, {
                ...filterParams,
                page: 1,
                limit: TASKS_PER_PAGE
            }).then(({ tasks, totalCount, hasMore, group }) => {
                setGroupTasks(prevGt => ({
                    ...prevGt,
                    [groupId]: {
                        tasks: tasks || [],
                        page: 1,
                        hasMore: hasMore || false,
                        totalCount: totalCount || 0,
                        ...(group?.tag && { tag: group.tag }),
                        ...(group?.user && { user: group.user }),
                        ...(group?.release && { release: group.release }),
                        ...(group?.withLinks !== undefined && { withLinks: group.withLinks })
                    }
                }));
                setExpandedGroups(prev => new Set(prev).add(groupId));
            }).catch(err => console.error('Error fetching group tasks:', err));
        } else {
            // Group already has tasks: toggle expand/collapse
            setExpandedGroups(prev => {
                const newSet = new Set(prev);
                if (newSet.has(groupId)) newSet.delete(groupId);
                else newSet.add(groupId);
                return newSet;
            });
        }
    }, [groupingMode, projectId, filterParams, groupTasks]);

    const onLoadMoreGroup = useCallback((groupId) => {
        const gt = groupTasks[groupId];
        if (!gt || !gt.hasMore || loadingMoreGroupId) return;
        const nextPage = (gt.page || 1) + 1;
        const apiGroupingMode = GROUPING_MODE_MAP[groupingMode];
        if (!apiGroupingMode || !projectId) return;

        setLoadingMoreGroupId(groupId);
        taskApiV2.getGroupTasks(projectId, apiGroupingMode, groupId, {
            ...filterParams,
            page: nextPage,
            limit: TASKS_PER_PAGE
        })
            .then(({ tasks, hasMore }) => {
                setGroupTasks(prev => {
                    const prevTasks = prev[groupId]?.tasks || [];
                    const existingIds = new Set(prevTasks.map(t => String(t.id)));
                    const toAppend = (tasks || []).filter(t => !existingIds.has(String(t.id)));
                    return {
                        ...prev,
                        [groupId]: {
                            ...prev[groupId],
                            tasks: [...prevTasks, ...toAppend],
                            page: nextPage,
                            hasMore: hasMore || false
                        }
                    };
                });
            })
            .catch(err => console.error('Error loading more group tasks:', err))
            .finally(() => setLoadingMoreGroupId(null));
    }, [groupingMode, projectId, filterParams, groupTasks, loadingMoreGroupId]);

    const onLoadMoreFlat = useCallback(() => {
        if (!ungroupedHasMore || loadingMoreFlat || groupingMode !== 'none') return;

        setLoadingMoreFlat(true);
        const nextPage = ungroupedPage + 1;
        taskApiV2.getProjectTasks(projectId, {
            ...filterParams,
            page: nextPage,
            limit: TASKS_PER_PAGE
        })
            .then(({ tasks, hasMore }) => {
                setUngroupedTasks(prev => {
                    const existingIds = new Set(prev.map(t => String(t.id)));
                    const toAppend = (tasks || []).filter(t => !existingIds.has(String(t.id)));
                    return [...prev, ...toAppend];
                });
                setUngroupedPage(nextPage);
                setUngroupedHasMore(hasMore || false);
            })
            .catch(err => console.error('Error loading more tasks:', err))
            .finally(() => setLoadingMoreFlat(false));
    }, [projectId, filterParams, ungroupedHasMore, ungroupedPage, groupingMode, loadingMoreFlat]);

    // Build groupedTasks, groupedTasksByWorkPlan, groupedTasksByReleases, groupedTasksByLinks from groupTasks + groups
    const { groupedTasks, groupedTasksByWorkPlan, groupedTasksByReleases, groupedTasksByLinks } = useMemo(() => {
        const empty = { all: { tag: null, user: null, release: null, withLinks: null, tasks: [], totalCount: 0, hasMore: false } };
        if (groupingMode === 'none') {
            return {
                groupedTasks: { all: { tag: null, tasks: [], totalCount: 0, hasMore: false } },
                groupedTasksByWorkPlan: { all: { user: null, tasks: [], totalCount: 0, hasMore: false } },
                groupedTasksByReleases: { all: { release: null, tasks: [], totalCount: 0, hasMore: false } },
                groupedTasksByLinks: { all: { withLinks: null, tasks: [], totalCount: 0, hasMore: false } }
            };
        }

        const result = {};
        groups.forEach(g => {
            const groupId = g.groupId;
            const gt = groupTasks[groupId];
            const rawTasks = gt?.tasks || [];
            const { ordered: tasks } = buildTaskTreeOrder(rawTasks);
            const totalCount = gt?.totalCount ?? g.count ?? 0;
            const hasMore = gt?.hasMore ?? false;
            if (groupingMode === 'feature-tag') {
                result[groupId] = { tag: g.tag || gt?.tag, tasks, totalCount, hasMore };
            } else if (groupingMode === 'work-plan') {
                result[groupId] = { user: g.user || gt?.user, tasks, totalCount, hasMore };
            } else if (groupingMode === 'releases') {
                result[groupId] = { release: g.release || gt?.release, tasks, totalCount, hasMore };
            } else if (groupingMode === 'task-links') {
                result[groupId] = { withLinks: g.withLinks ?? gt?.withLinks, tasks, totalCount, hasMore };
            }
        });

        return {
            groupedTasks: groupingMode === 'feature-tag' ? result : empty,
            groupedTasksByWorkPlan: groupingMode === 'work-plan' ? result : empty,
            groupedTasksByReleases: groupingMode === 'releases' ? result : empty,
            groupedTasksByLinks: groupingMode === 'task-links' ? result : empty
        };
    }, [groupingMode, groups, groupTasks]);

    // For ungrouped mode: use ungroupedTasks with buildTaskTreeOrder
    const { ordered: tasksInTreeOrder, subtaskIds } = useMemo(() => {
        if (groupingMode === 'none') return buildTaskTreeOrder(ungroupedTasks);
        const merged = new Set();
        groups.forEach(g => {
            const gt = groupTasks[g.groupId];
            const rawTasks = gt?.tasks || [];
            const { subtaskIds: sid } = buildTaskTreeOrder(rawTasks);
            sid.forEach(id => merged.add(id));
        });
        return { ordered: [], subtaskIds: merged };
    }, [groupingMode, ungroupedTasks, groups, groupTasks]);

    // allTasks: collect all loaded tasks from ungroupedTasks or from all groupTasks
    const allTasks = useMemo(() => {
        if (groupingMode === 'none') return ungroupedTasks;
        return Object.values(groupTasks).flatMap(gt => gt.tasks || []);
    }, [groupingMode, ungroupedTasks, groupTasks]);

    // taskStats and tagAnalytics: use loaded tasks only (partial data for v2)
    const taskStats = useMemo(() => {
        const total = allTasks.length;
        const open = allTasks.filter(task => task.status === 'open').length;
        const inProgress = allTasks.filter(task => task.status === 'in-progress').length;
        const completed = allTasks.filter(task => task.status === 'completed').length;
        const released = allTasks.filter(task => task.status === 'released').length;
        const completionPercentage = total > 0 ? Math.round(((completed + released) / total) * 100) : 0;
        return { total, open, inProgress, completed, released, completionPercentage };
    }, [allTasks]);

    const tagAnalytics = useMemo(() => {
        const now = new Date();
        let overdueCount = 0;
        let upcomingCount = 0;
        let totalCompletion = 0;
        let tagsWithTasks = 0;
        let totalHours = 0;
        let completedHours = 0;

        featureTags.forEach(tag => {
            if (tag.due_date) {
                const dueDate = new Date(tag.due_date);
                const daysRemaining = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
                if (daysRemaining < 0) overdueCount++;
                else if (daysRemaining <= 7) upcomingCount++;
            }
            const tagTasks = allTasks.filter(task => task.feature_tag?.id === tag.id);
            if (tagTasks.length > 0) {
                const completedTasks = tagTasks.filter(task => task.status === 'completed').length;
                totalCompletion += (completedTasks / tagTasks.length) * 100;
                tagsWithTasks++;
                const tagHours = tagTasks.reduce((sum, task) => sum + (parseFloat(task.hours) || 0), 0);
                const tagCompletedHours = tagTasks.filter(task => task.status === 'completed').reduce((sum, task) => sum + (parseFloat(task.hours) || 0), 0);
                totalHours += tagHours;
                completedHours += tagCompletedHours;
            }
        });

        const averageCompletion = tagsWithTasks > 0 ? Math.round(totalCompletion / tagsWithTasks) : 0;
        return {
            overdueCount,
            upcomingCount,
            averageCompletion,
            totalTags: featureTags.length,
            totalHours: Math.round(totalHours * 10) / 10,
            completedHours: Math.round(completedHours * 10) / 10
        };
    }, [featureTags, allTasks]);

    const filteredUsers = useMemo(() => users.filter(u => u.role !== 'unknown'), [users]);

    const calculateTaskSummary = useCallback((task) => {
        if (!task.work_plan_tasks || task.work_plan_tasks.length === 0) {
            return { totalHours: 0, occurrences: 0, work_plan_attachments: [] };
        }
        const totalHours = task.work_plan_tasks.reduce((sum, wpt) => sum + (parseFloat(wpt.hours) || 0), 0);
        const occurrences = task.work_plan_tasks.length;
        const attachments = [];
        task.work_plan_tasks.forEach(wpt => {
            if (wpt.work_plan_attachments?.length) attachments.push(...wpt.work_plan_attachments);
        });
        return { totalHours: totalHours.toFixed(1), occurrences, work_plan_attachments: attachments };
    }, []);

    const openTaskSummaryModal = useCallback((task) => {
        const summary = calculateTaskSummary(task);
        setSelectedTaskForModal({ ...task, ...summary });
    }, [calculateTaskSummary]);

    const closeTaskSummaryModal = useCallback(() => setSelectedTaskForModal(null), []);

    const getTaskStatus = useCallback((taskId) => {
        const task = allTasks.find(t => t.id === taskId);
        return task?.status || 'open';
    }, [allTasks]);

    const getStatusVariant = useCallback((status) => {
        switch (status?.toLowerCase()) {
            case 'in-progress': return 'warning';
            case 'completed':
            case 'released': return 'success';
            case 'open': return 'neutral';
            default: return 'neutral';
        }
    }, []);

    const getTaskHistory = useCallback((taskId) => {
        const task = allTasks.find(t => t.id === taskId);
        if (!task?.work_plan_tasks?.length) return [];
        return task.work_plan_tasks
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .map(wpt => ({
                date: new Date(wpt.created_at),
                hours: wpt.hours || 0,
                notes: wpt.notes || '',
                status: wpt.status === 'completed' ? 'Completed' : 'In Progress',
                attachments: wpt.work_plan_attachments || []
            }));
    }, [allTasks]);

    const isFeatureOverdue = useCallback((featureTag) => {
        if (!featureTag?.due_date) return false;
        return new Date(featureTag.due_date) < new Date();
    }, []);

    const handleTaskCreated = async (formData) => {
        try {
            let response;
            if (formData instanceof FormData) {
                formData.append('project_id', projectId);
                response = await taskApi.create(formData);
            } else {
                response = await taskApi.create({ ...formData, project_id: projectId });
            }

            if (groupingMode === 'none') {
                setUngroupedTasks(prev => [response, ...prev]);
                setUngroupedTotalCount(prev => prev + 1);
            }

            const parentId = createSubtaskForTaskId;
            if (parentId) {
                try {
                    await taskApi.createLink(parentId, response.id, 'subtask');
                } catch (linkErr) {
                    console.error('Error linking subtask:', linkErr);
                }
                setCreateSubtaskForTaskId(null);
            }
            setShowTaskForm(false);
        } catch (error) {
            console.error('Error creating task:', error);
        }
    };

    const handleTaskStatusChange = async (taskId, newStatus) => {
        try {
            const response = await api.patch(`/api/tasks/${taskId}/status`, { status: newStatus });
            const updateTask = (prev) => prev.map(t => t.id === taskId ? response.data : t);
            if (groupingMode === 'none') {
                setUngroupedTasks(updateTask);
            } else {
                setGroupTasks(prev => {
                    const next = { ...prev };
                    Object.keys(next).forEach(gid => {
                        const gt = next[gid];
                        if (gt.tasks?.some(t => t.id === taskId)) {
                            next[gid] = { ...gt, tasks: gt.tasks.map(t => t.id === taskId ? response.data : t) };
                        }
                    });
                    return next;
                });
            }
        } catch (err) {
            console.error('Error updating task status:', err);
        }
    };

    const handleAssigneeChange = async (taskId, newAssigneeId) => {
        try {
            const response = await api.patch(`/api/tasks/${taskId}`, { assigned_to: newAssigneeId });
            const updateTask = (prev) => prev.map(t => t.id === taskId ? response.data : t);
            if (groupingMode === 'none') {
                setUngroupedTasks(updateTask);
            } else {
                setGroupTasks(prev => {
                    const next = { ...prev };
                    Object.keys(next).forEach(gid => {
                        const gt = next[gid];
                        if (gt.tasks?.some(t => t.id === taskId)) {
                            next[gid] = { ...gt, tasks: gt.tasks.map(t => t.id === taskId ? response.data : t) };
                        }
                    });
                    return next;
                });
            }
        } catch (err) {
            console.error('Error updating task assignee:', err);
        }
    };

    const handleFiltersChange = (newFilters) => setFilters(newFilters);

    const handleStatusToggle = (status) => {
        setFilters(prev => {
            const current = prev.status || [];
            const newStatuses = current.includes(status) ? current.filter(s => s !== status) : [...current, status];
            return { ...prev, status: newStatuses };
        });
    };

    const statusOptions = [
        { value: 'open', label: 'Open' },
        { value: 'in-progress', label: 'In Progress' },
        { value: 'completed', label: 'Completed' },
        { value: 'released', label: 'Released' }
    ];

    const clearFilters = () => {
        setFilters({ status: [], createdBy: '', assignedTo: '', taskType: '', withLinksOnly: false });
        setSelectedTagId(null);
        setSearchQuery('');
        setStatusDropdownOpen(false);
    };

    const toggleSortOrder = () => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');

    const handleTagUpdate = async (taskId, tagId) => {
        try {
            const response = await api.patch(`/api/tasks/${taskId}`, { feature_tag_id: tagId });
            const updateTask = (prev) => prev.map(t => t.id === taskId ? response.data : t);
            if (groupingMode === 'none') {
                setUngroupedTasks(updateTask);
            } else {
                setGroupTasks(prev => {
                    const next = { ...prev };
                    Object.keys(next).forEach(gid => {
                        const gt = next[gid];
                        if (gt.tasks?.some(t => t.id === taskId)) {
                            next[gid] = { ...gt, tasks: gt.tasks.map(t => t.id === taskId ? response.data : t) };
                        }
                    });
                    return next;
                });
            }
        } catch (err) {
            console.error('Error updating task tag:', err);
        }
    };

    const handleTagCreate = (newTag) => {
        setFeatureTags(prev => (prev.find(t => t.id === newTag.id) ? prev : [...prev, newTag]));
    };

    const handleFeatureTagUpdate = (updatedTag) => {
        setFeatureTags(prev => prev.map(t => t.id === updatedTag.id ? updatedTag : t));
    };

    const untaggedCount = allTasks.filter(t => !t.feature_tag?.id).length;

    const getSelectedTagName = () => {
        if (selectedTagId === null) return null;
        if (selectedTagId === 'untagged') return 'Untagged';
        return featureTags.find(t => t.id === selectedTagId)?.name || null;
    };

    const getActiveFilters = () => {
        const active = [];
        if (filters.status?.length) {
            active.push({ label: 'Status', value: filters.status.map(s => s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')).join(', ') });
        }
        if (filters.createdBy) {
            const u = filteredUsers.find(x => x.id === filters.createdBy);
            active.push({ label: 'Created By', value: u?.display_name || u?.email || 'Unknown' });
        }
        if (filters.assignedTo) {
            active.push({ label: 'Assigned To', value: filters.assignedTo === 'unassigned' ? 'Unassigned' : (filteredUsers.find(x => x.id === filters.assignedTo)?.display_name || 'Unknown') });
        }
        if (filters.taskType) active.push({ label: 'Type', value: filters.taskType });
        return active;
    };

    const handleTagDelete = (tagId) => {
        setFeatureTags(prev => prev.filter(t => t.id !== tagId));
        fetchProjectData();
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) setStatusDropdownOpen(false);
        };
        if (statusDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [statusDropdownOpen]);

    useEffect(() => {
        if (showTaskForm && taskFormTitleRef.current) {
            setTimeout(() => taskFormTitleRef.current?.focus(), 100);
        }
        const handleEscape = (e) => {
            if (e.key === 'Escape' && showTaskForm) setShowTaskForm(false);
        };
        if (showTaskForm) document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [showTaskForm]);

    const handleStatsToggle = () => {
        if (statsCollapsed) {
            setFeatureTagStatsCollapsed(true);
            setTaskTimelineCollapsed(true);
        }
        setStatsCollapsed(!statsCollapsed);
    };

    const handleFeatureTagStatsToggle = () => {
        if (featureTagStatsCollapsed) {
            setStatsCollapsed(true);
            setTaskTimelineCollapsed(true);
        }
        setFeatureTagStatsCollapsed(!featureTagStatsCollapsed);
    };

    const handleTaskTimelineToggle = () => {
        if (taskTimelineCollapsed) {
            setStatsCollapsed(true);
            setFeatureTagStatsCollapsed(true);
        }
        setTaskTimelineCollapsed(!taskTimelineCollapsed);
    };

    // V2: Do NOT auto-expand groups - keep all task-group headers collapsed until user explicitly expands

    const setTasks = useCallback((updater) => {
        if (groupingMode === 'none') {
            setUngroupedTasks(prev => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                return Array.isArray(next) ? next : prev;
            });
        } else {
            setGroupTasks(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(gid => {
                    const gt = next[gid];
                    if (typeof updater === 'function') {
                        const updated = updater(gt.tasks || []);
                        if (Array.isArray(updated)) next[gid] = { ...gt, tasks: updated };
                    }
                });
                return next;
            });
        }
    }, [groupingMode]);

    if (loading) {
        return (
            <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading project details...</p>
            </div>
        );
    }

    if (error) return <div className="p-4 text-red-600">{error}</div>;
    if (!project) return <div className="p-4">Project not found</div>;

    const filterSummaryCount = groupingMode === 'none'
        ? ungroupedTasks.length
        : Object.values(groupTasks).reduce((sum, gt) => sum + (gt.tasks?.length || 0), 0);
    const filterTotalCount = groupingMode === 'none' ? ungroupedTotalCount : Object.values(groupTasks).reduce((sum, gt) => sum + (gt.totalCount || 0), 0);

    const hasActiveFilters = !!(filters.status?.length || filters.createdBy || filters.assignedTo || filters.taskType || filters.withLinksOnly || selectedTagId || searchQuery);
    const activeFilters = getActiveFilters();
    const selectedTagName = getSelectedTagName();
    const groupingLabel = GROUPING_MODE_LABELS[groupingMode] || 'Ungrouped';
    const groupCount = groupingMode !== 'none' ? groups.length : 0;

    return (
        <div className="project-details">
            <div className="project-header">
                <div className="project-header-top">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <Button variant="neutral-tertiary" icon={<FeatherArrowLeft />} onClick={() => navigate('/projects')} className="back-button">
                            Back to Projects
                        </Button>
                        <Button
                            variant="neutral-tertiary"
                            onClick={() => navigate(`/projects/${projectId}/legacy`)}
                            className="back-button"
                            title="Open the original project details (loads all tasks at once)"
                        >
                            Legacy view
                        </Button>
                    </div>
                </div>
                <div className="project-header-content">
                    <div>
                        <h1 className="project-title">{project.name}</h1>
                        <p className="project-description">{project.description}</p>
                        {tagAnalytics.totalTags > 0 && (
                            <div className="project-analytics-summary">
                                <div className="analytics-item">
                                    <span className="analytics-label">Features:</span>
                                    <span className="analytics-value">{tagAnalytics.totalTags}</span>
                                </div>
                                <div className="analytics-item">
                                    <span className="analytics-label">Avg. Progress:</span>
                                    <span className="analytics-value">{tagAnalytics.averageCompletion}%</span>
                                </div>
                                <div className="analytics-item">
                                    <span className="analytics-label">Total Hours:</span>
                                    <span className="analytics-value">{tagAnalytics.totalHours}h</span>
                                </div>
                                <div className="analytics-item">
                                    <span className="analytics-label">Completed:</span>
                                    <span className="analytics-value">{tagAnalytics.completedHours}h</span>
                                </div>
                                {tagAnalytics.overdueCount > 0 && (
                                    <div className="analytics-item overdue">
                                        <span className="analytics-label">Overdue:</span>
                                        <span className="analytics-value">{tagAnalytics.overdueCount}</span>
                                    </div>
                                )}
                                {tagAnalytics.upcomingCount > 0 && (
                                    <div className="analytics-item upcoming">
                                        <span className="analytics-label">Due Soon:</span>
                                        <span className="analytics-value">{tagAnalytics.upcomingCount}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {userIsManager && (
                        <div className="project-header-actions">
                            <button onClick={() => navigate(`/projects/${projectId}/activity-history`)} className="activity-history-button" title="View Activity History">
                                <Activity size={16} /> Activity History
                            </button>
                            {activeTab === 'tasks' && (
                                <button onClick={() => setShowTaskForm(true)} className="button primary create-task-button">Create Task</button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="project-tabs">
                <button className={`project-tab ${activeTab === 'tasks' ? 'active' : ''}`} onClick={() => setActiveTab('tasks')}><BarChart size={16} /> Tasks</button>
                <button className={`project-tab ${activeTab === 'billing' ? 'active' : ''}`} onClick={() => setActiveTab('billing')}><DollarSign size={16} /> Billing</button>
                <button className={`project-tab ${activeTab === 'releases' ? 'active' : ''}`} onClick={() => setActiveTab('releases')}><Package size={16} /> Releases</button>
                <button className={`project-tab ${activeTab === 'notes-artifacts' ? 'active' : ''}`} onClick={() => setActiveTab('notes-artifacts')}><FileText size={16} /> Notes & Artifacts</button>
            </div>

            {activeTab === 'billing' && <BillingTab projectId={projectId} projectName={project?.name} users={users} />}

            {activeTab === 'releases' && (
                <div>
                    {userIsManager && (
                        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button onClick={() => navigate(`/projects/${projectId}/releases`)} className="button secondary release-management-button">Release Management</button>
                        </div>
                    )}
                    <ReleaseManagementStatsTab projectId={projectId} projectName={project?.name} tasks={allTasks} />
                </div>
            )}

            {activeTab === 'notes-artifacts' && <ProjectNotesArtifacts projectId={projectId} project={project} />}

            {activeTab === 'tasks' && (
                <>
                    <div className="unified-section-headers">
                        <div className="unified-header-item" onClick={handleStatsToggle} style={{ cursor: 'pointer' }}>
                            <h3 className="unified-header-title"><BarChart size={16} /> Task Overview</h3>
                            <button className="unified-header-toggle" onClick={(e) => { e.stopPropagation(); handleStatsToggle(); }} aria-label={statsCollapsed ? 'Expand' : 'Collapse'}>
                                {statsCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                            </button>
                        </div>
                        <div className="unified-header-item" onClick={handleFeatureTagStatsToggle} style={{ cursor: 'pointer' }}>
                            <h3 className="unified-header-title"><BarChart size={16} /> Feature Tag Stats</h3>
                            <button className="unified-header-toggle" onClick={(e) => { e.stopPropagation(); handleFeatureTagStatsToggle(); }} aria-label={featureTagStatsCollapsed ? 'Expand' : 'Collapse'}>
                                {featureTagStatsCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                            </button>
                        </div>
                        <div className="unified-header-item" onClick={handleTaskTimelineToggle} style={{ cursor: 'pointer' }}>
                            <h3 className="unified-header-title"><BarChart size={16} /> Task-Feature Timeline</h3>
                            <button className="unified-header-toggle" onClick={(e) => { e.stopPropagation(); handleTaskTimelineToggle(); }} aria-label={taskTimelineCollapsed ? 'Expand' : 'Collapse'}>
                                {taskTimelineCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                            </button>
                        </div>
                    </div>

                    <div className={`task-statistics ${statsCollapsed ? 'section-collapsed' : ''}`}>
                        <div className={`stats-grid ${statsCollapsed ? 'collapsed' : ''}`}>
                            <div className="stat-card stat-open" title={`${taskStats.open} Open Tasks`}>
                                <div className="stat-icon"><AlertCircle size={18} strokeWidth={2} /></div>
                                <div className="stat-content"><div className="stat-number">{taskStats.open}</div><div className="stat-label">Open</div></div>
                            </div>
                            <div className="stat-card stat-in-progress" title={`${taskStats.inProgress} In Progress Tasks`}>
                                <div className="stat-icon"><Clock size={18} strokeWidth={2} /></div>
                                <div className="stat-content"><div className="stat-number">{taskStats.inProgress}</div><div className="stat-label">In Progress</div></div>
                            </div>
                            <div className="stat-card stat-completed" title={`${taskStats.completed} Completed Tasks`}>
                                <div className="stat-icon"><CheckCircle size={18} strokeWidth={2} /></div>
                                <div className="stat-content"><div className="stat-number">{taskStats.completed}</div><div className="stat-label">Completed</div></div>
                            </div>
                            <div className="stat-card stat-total" title={`${taskStats.total} Total Tasks`}>
                                <div className="stat-icon"><BarChart size={18} strokeWidth={2} /></div>
                                <div className="stat-content"><div className="stat-number">{taskStats.total}</div><div className="stat-label">Total</div></div>
                            </div>
                        </div>
                    </div>

                    <div className={`feature-tag-stats-wrapper ${featureTagStatsCollapsed ? 'section-collapsed' : ''}`}>
                        <FeatureTagStats featureTags={featureTags} tasks={allTasks} isCollapsed={featureTagStatsCollapsed} onToggleCollapse={handleFeatureTagStatsToggle} />
                    </div>

                    <div className={`task-feature-timeline-wrapper ${taskTimelineCollapsed ? 'section-collapsed' : ''}`}>
                        <TaskFeatureTimeline featureTags={featureTags} tasks={allTasks} onTaskClick={openTaskSummaryModal} isCollapsed={taskTimelineCollapsed} onToggleCollapse={handleTaskTimelineToggle} />
                    </div>

                    <div className="tag-bar-section">
                        <div className="tag-bar-header" onClick={() => setTagBarCollapsed(!tagBarCollapsed)} style={{ cursor: 'pointer' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                                <h3 className="tag-bar-title"><Tag size={16} /> Feature Tags</h3>
                                {tagBarCollapsed && selectedTagId !== null && <span className="section-badge">{getSelectedTagName()}</span>}
                            </div>
                            <button className="stats-toggle-btn" onClick={(e) => { e.stopPropagation(); setTagBarCollapsed(!tagBarCollapsed); }} aria-label={tagBarCollapsed ? 'Expand tags' : 'Collapse tags'}>
                                {tagBarCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                            </button>
                        </div>
                        <div className={`tag-bar-wrapper ${tagBarCollapsed ? 'collapsed' : ''}`}>
                            <TagBar tags={featureTags} selectedTagId={selectedTagId} onTagSelect={setSelectedTagId} onTagCreate={handleTagCreate} onTagUpdate={handleFeatureTagUpdate} onTagDelete={handleTagDelete} projectId={projectId} untaggedCount={untaggedCount} tasks={allTasks} users={users} />
                        </div>
                    </div>

                    <div className="task-controls-section">
                        <div className="task-controls-header" onClick={() => setTaskControlsCollapsed(!taskControlsCollapsed)} style={{ cursor: 'pointer' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, flexWrap: 'wrap' }}>
                                <h3 className="task-controls-title"><Filter size={16} /> Filters & Controls</h3>
                                {taskControlsCollapsed && getActiveFilters().length > 0 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                                        {getActiveFilters().map((f, i) => <span key={i} className="section-badge">{f.value}</span>)}
                                    </div>
                                )}
                            </div>
                            <button className="stats-toggle-btn" onClick={(e) => { e.stopPropagation(); setTaskControlsCollapsed(!taskControlsCollapsed); }} aria-label={taskControlsCollapsed ? 'Expand controls' : 'Collapse controls'}>
                                {taskControlsCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                            </button>
                        </div>
                        <div className={`task-controls ${taskControlsCollapsed ? 'collapsed' : ''}`}>
                            <div className="controls-left">
                                <div className="view-toggle">
                                    <IconButton icon={<Grid size={16} />} variant={viewMode === 'grid' ? 'brand-primary' : 'neutral-tertiary'} onClick={() => setViewMode('grid')} title="Grid View" aria-pressed={viewMode === 'grid'} />
                                    <IconButton icon={<List size={16} />} variant={viewMode === 'list' ? 'brand-primary' : 'neutral-tertiary'} onClick={() => setViewMode('list')} title="List View" aria-pressed={viewMode === 'list'} />
                                    <IconButton icon={<Layout size={16} />} variant={viewMode === 'compact' ? 'brand-primary' : 'neutral-tertiary'} onClick={() => setViewMode('compact')} title="Compact View" aria-pressed={viewMode === 'compact'} />
                                    {viewMode === 'compact' && (
                                        <IconButton icon={<Circle size={16} />} variant={subtaskBubblesMode ? 'brand-primary' : 'neutral-tertiary'} onClick={() => setSubtaskBubblesMode(!subtaskBubblesMode)} title="Subtask Bubbles" aria-pressed={subtaskBubblesMode} />
                                    )}
                                </div>
                                <div className="sort-control">
                                    <IconButton icon={<TrendingDown size={16} />} variant="neutral-tertiary" onClick={toggleSortOrder} title={`Sort ${sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}`} />
                                    <span className="sort-label">{sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}</span>
                                </div>
                                <div className="group-toggle-control">
                                    <button className={`group-mode-btn ${groupingMode === 'none' ? 'active' : ''}`} onClick={() => setGroupingMode('none')} title="Ungrouped"><List size={14} /><span>Ungrouped</span></button>
                                    <button className={`group-mode-btn ${groupingMode === 'feature-tag' ? 'active' : ''}`} onClick={() => setGroupingMode('feature-tag')} title="Group by Feature Tags"><Tag size={14} /><span>By Features</span></button>
                                    <button className={`group-mode-btn ${groupingMode === 'work-plan' ? 'active' : ''}`} onClick={() => setGroupingMode('work-plan')} title="Group by Work Plan"><CheckCircle size={14} /><span>PickedUp Today</span></button>
                                    <button className={`group-mode-btn ${groupingMode === 'releases' ? 'active' : ''}`} onClick={() => setGroupingMode('releases')} title="Group by Releases"><Package size={14} /><span>By Releases</span></button>
                                    {/* <button className={`group-mode-btn ${groupingMode === 'task-links' ? 'active' : ''}`} onClick={() => setGroupingMode('task-links')} title="Group by Task Links"><Link size={14} /><span>By Links</span></button> */}
                                </div>
                            </div>
                            <div className="controls-right">
                                <div className="filter-controls">
                                    <div className="filter-group">
                                        <label htmlFor="filter-status" className="filter-label"><span className="filter-label-text">Status</span></label>
                                        <div className="filter-select-wrapper status-multiselect-wrapper" ref={statusDropdownRef}>
                                            <div className="status-multiselect-trigger" onClick={() => setStatusDropdownOpen(!statusDropdownOpen)} tabIndex={0} onKeyDown={(e) => e.key === 'Escape' && setStatusDropdownOpen(false)}>
                                                <span className="status-multiselect-display">{filters.status?.length ? `${filters.status.length} selected` : 'All Statuses'}</span>
                                                <ChevronDown size={14} className={`status-arrow ${statusDropdownOpen ? 'open' : ''}`} />
                                            </div>
                                            {statusDropdownOpen && (
                                                <div className="status-multiselect-dropdown">
                                                    {statusOptions.map(opt => (
                                                        <label key={opt.value} className="status-multiselect-option">
                                                            <input type="checkbox" checked={filters.status?.includes(opt.value)} onChange={() => handleStatusToggle(opt.value)} onClick={(e) => e.stopPropagation()} />
                                                            <span>{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="filter-group">
                                        <label htmlFor="filter-created-by" className="filter-label"><span className="filter-label-text">Created By</span></label>
                                        <div className="filter-select-wrapper">
                                            <select id="filter-created-by" value={filters.createdBy} onChange={(e) => handleFiltersChange({ ...filters, createdBy: e.target.value })} className="filter-select">
                                                <option value="">All Users</option>
                                                {filteredUsers.map(u => <option key={u.id} value={u.id}>{u.display_name || u.email}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="filter-group">
                                        <label htmlFor="filter-assigned-to" className="filter-label"><span className="filter-label-text">Assigned To</span></label>
                                        <div className="filter-select-wrapper">
                                            <select id="filter-assigned-to" value={filters.assignedTo} onChange={(e) => handleFiltersChange({ ...filters, assignedTo: e.target.value })} className="filter-select">
                                                <option value="">All Users</option>
                                                <option value="unassigned">Unassigned</option>
                                                {filteredUsers.map(u => <option key={u.id} value={u.id}>{u.display_name || u.email}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="filter-group">
                                        <label htmlFor="filter-task-type" className="filter-label"><span className="filter-label-text">Task Type</span></label>
                                        <div className="filter-select-wrapper">
                                            <TaskTypeSelect id="filter-task-type" value={filters.taskType} onChange={(e) => handleFiltersChange({ ...filters, taskType: e.target.value })} className="filter-select" includeAllOption />
                                        </div>
                                    </div>
                                    {groupingMode !== 'task-links' && (
                                        <div className="filter-group filter-group-checkbox">
                                            <label className="filter-label filter-checkbox-label">
                                                <input type="checkbox" checked={filters.withLinksOnly || false} onChange={(e) => handleFiltersChange({ ...filters, withLinksOnly: e.target.checked })} className="filter-checkbox" />
                                                <span className="filter-label-text">Only tasks with links</span>
                                            </label>
                                        </div>
                                    )}
                                    {((filters.status?.length) || filters.createdBy || filters.assignedTo || filters.taskType || filters.withLinksOnly || selectedTagId) && (
                                        <button className="clear-filters-btn" onClick={clearFilters} title="Clear all filters"><span>Clear</span></button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="task-search-section">
                        <div className="task-search-wrapper">
                            <Search size={18} className="task-search-icon" />
                            <input type="text" className="task-search-input" placeholder="Search tasks by title, description, ID, assignee, or tag..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                            {searchQuery && <button className="task-search-clear" onClick={() => setSearchQuery('')} title="Clear search">×</button>}
                        </div>
                    </div>

                    <div className="filter-summary">
                        <div className="filter-summary-row">
                            <span className="filter-summary-grouping">{groupingLabel}</span>
                            {groupCount > 0 && <span className="filter-summary-group-count">{groupCount} group{groupCount !== 1 ? 's' : ''}</span>}
                            <span className="filter-summary-separator">·</span>
                            <span className="filter-summary-text">
                                {hasActiveFilters
                                    ? `Showing ${filterSummaryCount} of ${filterTotalCount} tasks`
                                    : `${filterSummaryCount} of ${filterTotalCount} tasks loaded`}
                            </span>
                        </div>
                        {(hasActiveFilters || searchQuery) && (
                            <div className="filter-summary-chips">
                                {searchQuery && (
                                    <span className="filter-chip">
                                        <Search size={12} />
                                        <span>"{searchQuery}"</span>
                                        <button className="filter-chip-clear" onClick={() => setSearchQuery('')} title="Clear search">×</button>
                                    </span>
                                )}
                                {activeFilters.map((f, i) => (
                                    <span key={i} className="filter-chip">
                                        <span className="filter-chip-label">{f.label}:</span>
                                        <span>{f.value}</span>
                                    </span>
                                ))}
                                {selectedTagName && (
                                    <span className="filter-chip">
                                        <Tag size={12} />
                                        <span>{selectedTagName}</span>
                                        <button className="filter-chip-clear" onClick={() => setSelectedTagId(null)} title="Clear tag filter">×</button>
                                    </span>
                                )}
                                {filters.withLinksOnly && (
                                    <span className="filter-chip">
                                        <Link size={12} />
                                        <span>With Links Only</span>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {userIsManager && activeTab === 'tasks' && (
                        <button onClick={() => setShowTaskForm(true)} className={`floating-create-task-button ${showTaskForm ? 'hidden' : ''}`} aria-label="Create Task" title="Create Task">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        </button>
                    )}

                    {showTaskForm && (
                        <>
                            <div className="task-form-backdrop" />
                            <div className="task-form-side-panel">
                                <div className="task-form-side-panel-header">
                                    <h2>{createSubtaskForTaskId ? 'Create Subtask' : 'Create New Task'}</h2>
                                    <button className="task-form-side-panel-close" onClick={() => { setShowTaskForm(false); setCreateSubtaskForTaskId(null); }} aria-label="Close">×</button>
                                </div>
                                <div className="task-form-side-panel-content">
                                    <TaskForm key={createSubtaskForTaskId ?? 'new'} onSuccess={handleTaskCreated} onCancel={() => { setShowTaskForm(false); setCreateSubtaskForTaskId(null); }} users={users} isProjectTask={true} projectId={projectId} onTagCreate={handleTagCreate} availableTags={featureTags} titleInputRef={taskFormTitleRef} defaultTaskType={createSubtaskForTaskId ? TASK_TYPE_SUBTASK : DEFAULT_TASK_TYPE} parentTask={createSubtaskForTaskId ? allTasks.find(t => t.id === createSubtaskForTaskId) : null} onSwitchToStandalone={createSubtaskForTaskId ? () => setCreateSubtaskForTaskId(null) : null} />
                                </div>
                            </div>
                        </>
                    )}

                    {(groupingMode !== 'none' && groupsLoading) || (groupingMode === 'none' && tasksLoading) ? (
                        <div className="loading-state">
                            <div className="spinner" />
                            <p>{groupingMode !== 'none' ? 'Loading groups...' : 'Loading tasks...'}</p>
                        </div>
                    ) : (
                    <TaskList
                        tasks={groupingMode === 'none' ? tasksInTreeOrder : allTasks}
                        allTasks={allTasks}
                        subtaskIds={subtaskIds}
                        releases={releases}
                        setTasks={setTasks}
                        onStatusChange={handleTaskStatusChange}
                        onAssigneeChange={handleAssigneeChange}
                        users={users}
                        isManager={isManager}
                        viewMode={viewMode}
                        availableTags={featureTags}
                        projectId={projectId}
                        onTagUpdate={handleTagUpdate}
                        onTagCreate={handleTagCreate}
                        onTaskExplore={openTaskSummaryModal}
                        onAddSubtask={(task) => { setCreateSubtaskForTaskId(task.id); setShowTaskForm(true); setSelectedTaskForModal(null); }}
                        groupingMode={groupingMode}
                        groupedTasks={groupedTasks}
                        groupedTasksByWorkPlan={groupedTasksByWorkPlan}
                        groupedTasksByReleases={groupedTasksByReleases}
                        groupedTasksByLinks={groupedTasksByLinks}
                        expandedGroups={expandedGroups}
                        onToggleGroup={toggleGroup}
                        isFeatureOverdue={isFeatureOverdue}
                        allowCrossProject={project?.allow_cross_project_task_linking || false}
                        subtaskBubblesMode={subtaskBubblesMode}
                        onLoadMoreGroup={onLoadMoreGroup}
                        loadingMoreGroupId={loadingMoreGroupId}
                        hasMoreFlat={ungroupedHasMore}
                        onLoadMoreFlat={onLoadMoreFlat}
                        loadingMoreFlat={loadingMoreFlat}
                    />
                    )}

                    <TaskSummaryModal task={selectedTaskForModal} isOpen={!!selectedTaskForModal} onClose={closeTaskSummaryModal} getTaskStatus={getTaskStatus} getStatusVariant={getStatusVariant} getTaskHistory={getTaskHistory} availableTags={featureTags} allTasks={allTasks} projectId={projectId} allowCrossProject={project?.allow_cross_project_task_linking || false} isManager={isManager} onTaskClick={openTaskSummaryModal} onAddSubtask={(task) => { setCreateSubtaskForTaskId(task.id); setShowTaskForm(true); setSelectedTaskForModal(null); }} onTaskUpdate={(updatedTask) => { if (groupingMode === 'none') setUngroupedTasks(prev => prev.map(t => t.id === updatedTask.id ? { ...t, ...updatedTask } : t)); else setGroupTasks(prev => { const n = { ...prev }; Object.keys(n).forEach(gid => { const gt = n[gid]; if (gt.tasks?.some(t => t.id === updatedTask.id)) n[gid] = { ...gt, tasks: gt.tasks.map(t => t.id === updatedTask.id ? { ...t, ...updatedTask } : t) }; }); return n; }); setSelectedTaskForModal(prev => prev?.id === updatedTask.id ? { ...prev, ...updatedTask } : prev); }} onLinksUpdated={(taskId, links) => { if (groupingMode === 'none') setUngroupedTasks(prev => prev.map(t => t.id === taskId ? { ...t, linked_tasks: links } : t)); else setGroupTasks(prev => { const n = { ...prev }; Object.keys(n).forEach(gid => { const gt = n[gid]; if (gt.tasks?.some(t => t.id === taskId)) n[gid] = { ...gt, tasks: gt.tasks.map(t => t.id === taskId ? { ...t, linked_tasks: links } : t) }; }); return n; }); setSelectedTaskForModal(prev => prev?.id === taskId ? { ...prev, linked_tasks: links } : prev); }} />
                </>
            )}
        </div>
    );
};

export default ProjectDetailsV2;
